import React, { useState, useEffect } from 'react';
import { Tag, Switch } from 'antd';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, MenuItem } from '@material-ui/core';
import debounce from 'lodash/debounce';
import NoxTable from '../../component/Table';
import InnerHeader from '../../component/InnerHeader';
import Modal from '../../component/Modal/Modal';
import {
  fetchCustomType,
  setCustomTypeDraft,
  contentModalDuplicate,
  deleteCustomTypeAction
} from '../../actions/CustomTypeAction';
import Loader from '../../component/Loader/Loader';
import CButton from '../../component/Button';
import { CStandardInput } from '../../component/Input';

import Copy from '../../component/Copy';
import formatDate from '../../helpers/formatDate';
import { DefaultSelect } from '../../component/Select';
import { useGetContentModelsQuery } from '../../reactQuery/contentModels/queries/useGetContentModelsQuery';
import { useAddContentModelMutation } from '../../reactQuery/contentModels/mutations/useAddContntModelMutation';

const useStyles = makeStyles({
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  }
});

const infoBarStyle = {
  fontFamily: 'poppins',
  height: '22px',
  padding: '0 6px'
};

const ContentModel = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: contentModelData, refetch } = useGetContentModelsQuery();
  const mutation = useAddContentModelMutation({
    onSuccess: () => {
      refetch();
    }
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isSaveEnabled, setSaveEnabled] = useState(true);
  const [isDuplicateSaveEnabled, setDuplicateSaveEnabled] = useState(true);
  const [openNewContentModelModal, setOpenNewContentModelModal] = useState(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [apiId, setApiId] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [submit, setSubmit] = useState(false);
  // const [customTypeDraft, setCustomTypeDraft] = useState({})

  const types = ['public', 'private', 'protected'];

  const customTypeDraft = useSelector(({ customType }) => customType.customTypeDraft);
  const currentProject = useSelector(({ project }) => project.currentProject);

  const classes = useStyles();

  const openDetail = (item) => {
    history.push(`/content-model/${item._id}`);
  };

  const mapToTable = (customTypes) => {
    const data = [];
    if (!customTypes || customTypes.length === 0) {
      return data;
    }

    customTypes.forEach(({ _id, name, content, template, created, updated, isPublic }, index) => {
      const creationDate = formatDate(created);

      const updateDate = formatDate(updated ?? created); // this logic is not good, there is a logical mistake in old code, we were not using a "updated" field in db

      data.push({
        _id,
        by: 'me',
        content: content.length,
        contentIds: content,
        created: creationDate,
        fields: Object.keys(template || {}).length,
        key: index,
        name,
        status: isPublic ? 'Published' : 'Draft',
        updated: updateDate
      });
    });

    return data;
  };

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (name) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
            }}
          >
            <Copy text={name} />
          </div>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
      title: <div style={{ maxWidth: '37px' }}>Name</div>
    },
    {
      dataIndex: 'content',
      key: 'content',
      render: (content) => {
        return <Copy text={content} />;
      },
      sorter: (a, b) => a.content.toString().localeCompare(b.content),
      title: 'Content'
    },
    {
      dataIndex: 'fields',
      key: 'fields',
      render: (fields) => {
        return <Copy text={fields} />;
      },
      sorter: (a, b) => a.fields.toString().localeCompare(b.fields),
      title: 'Fields'
    },
    {
      dataIndex: 'created',
      key: 'created',
      render: (created) => <Copy text={created} />,
      sorter: (a, b) => a.created - b.created,
      title: 'Created'
    },
    {
      dataIndex: 'updated',
      key: 'updated',
      render: (updated) => <Copy text={updated} />,
      sorter: (a, b) => a.updated - b.updated,
      title: 'Updated'
    },
    {
      key: 'edit',
      render: (item) => {
        return <Tag onClick={() => openDetail(item)}>edit</Tag>;
      }
    }
  ];

  const validate = () => {
    const errors = {};
    if (apiIdObject.includes(apiId)) {
      errors.apiId = 'API ID is not unique';
    }
    if (nameObject.includes(customTypeDraft.name)) {
      errors.name = 'Name is not unique';
    }
    return errors;
  };

  const onSave = (e) => {
    e.preventDefault();
    setSubmit(true);
    setSaveEnabled(false);
    if (Object.keys(formErrors).length === 0) {
      mutation.mutate(customTypeDraft);
      // dispatch(createCustomTypeAction());
      setOpenNewContentModelModal(false);
      dispatch(
        setCustomTypeDraft({
          api_id: '',
          content: [],
          isPublic: true,
          name: '',
          project: currentProject ? currentProject._id : '',
          template: [],
          type: 'single',
          visibility: 'public'
        })
      );
      setApiId('');
      setSubmit(false);
    }
    setSaveEnabled(true);
  };

  const onSaveDuplicate = (e) => {
    e.preventDefault();
    setDuplicateSaveEnabled(false);
    // eslint-disable-next-line no-restricted-syntax
    for (const index of selectedRowKeys) {
      const item = data[index];

      dispatch(
        contentModalDuplicate(
          item._id,
          {
            api_id: customTypeDraft.api_id,
            name: customTypeDraft.name
          },
          () => dispatch(fetchCustomType())
        )
      );
    }
    setApiId('');
    dispatch(
      setCustomTypeDraft({
        api_id: '',
        content: [],
        isPublic: true,
        name: '',
        project: currentProject ? currentProject._id : '',
        template: [],
        type: 'single',
        visibility: 'public'
      })
    );

    setSelectedRowKeys([]);
    setDuplicateSaveEnabled(true);
    setOpenDuplicateModal(false);
  };

  const onNewContentModel = () => {
    setOpenNewContentModelModal(true);
  };

  const onNewContentModelCancel = () => {
    setOpenNewContentModelModal(false);
  };

  const onDuplicate = () => {
    setOpenDuplicateModal(true);
  };

  const onDuplicateCancel = () => {
    setOpenDuplicateModal(false);
  };

  const onCustomTypeDelete = async () => {
    await Promise.all(
      selectedRowKeys.map(async (index) => {
        const item = data[index];

        dispatch(deleteCustomTypeAction(item._id, item.contentIds));
      })
    );
    refetch();
    setSelectedRowKeys([]);
  };

  const actions = [
    {
      onClick: onNewContentModel,
      title: 'Create Content Model',
      type: 'primary'
    }
  ];

  useEffect(() => {
    async function load() {
      dispatch(fetchCustomType());
    }
    if (currentProject) {
      load();
    }

    async function setDraft() {
      dispatch(
        setCustomTypeDraft({
          api_id: '',
          content: [],
          isPublic: true,
          name: '',
          project: currentProject ? currentProject._id : '',
          template: {},
          type: 'single',
          visibility: 'public'
        })
      );
    }
    setDraft();
  }, [dispatch, currentProject]);

  const isLoaded = useSelector(({ customType }) => customType.isLoaded);
  const customTypes = useSelector(({ customType }) => customType.customTypes);
  const data = mapToTable(contentModelData);

  const filterList = ['All', ...new Set(data.map((item) => item.status))];
  const [selectedFilter, setSelectedFilter] = useState(filterList[0]);
  const onFilterChange = (value) => setSelectedFilter(value);

  const apiIdObject = customTypes?.map((item) => {
    return item.api_id;
  });

  const nameObject = customTypes?.map((item) => {
    return item.name;
  });

  if (selectedRowKeys.length) {
    const selectedRowNames = [];
    selectedRowKeys.forEach((index) => {
      selectedRowNames.push(data[index].name);
    });

    actions.unshift(
      {
        SelectedRows: selectedRowNames,
        onClick: onCustomTypeDelete,
        title: 'Delete',
        type: 'secondary'
      },
      {
        onClick: onDuplicate,
        title: 'Duplicate',
        type: 'secondary'
      }
    );
  }

  useEffect(() => {
    dispatch(
      setCustomTypeDraft({
        ...customTypeDraft,
        api_id: apiId
      })
    );
    setFormErrors(validate());
  }, [apiId]);

  const updateApiIdInput = (value) => {
    if (!/[^a-zA-Z0-9- ]/.test(value)) {
      setApiId(value);
      dispatch(
        setCustomTypeDraft({
          ...customTypeDraft,
          api_id: value
        })
      );
    }
  };

  const uniqueApiId = (value) => {
    return `${currentProject?.name} ${value}`
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
  };

  const handleNameChange = (value) => {
    if (!/[^a-zA-Z0-9- ]/.test(value)) {
      dispatch(
        setCustomTypeDraft({
          ...customTypeDraft,
          name: value,
          template: {}
        })
      );

      debounce(() => {
        setApiId(uniqueApiId(value));
      }, 1000)();
    }
  };

  if (!currentProject) {
    return <Loader />;
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={customTypes.length} title="Content Model" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={data}
        filter={filterList}
        loading={!isLoaded}
        onFilterChange={onFilterChange}
        pagination={20}
        selectedFilter={selectedFilter}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />

      <Modal
        footerActions={
          <>
            <CButton
              htmlType="submit"
              onClick={(e) => {
                e.preventDefault();
                setOpenDuplicateModal(false);
              }}
              size="large"
              type="secondary"
            >
              Cancel
            </CButton>
            <CButton
              disabled={!isSaveEnabled}
              htmlType="submit"
              onClick={(e) => onSave(e)}
              size="large"
              type="primary"
            >
              Create
            </CButton>
          </>
        }
        handleClose={onNewContentModelCancel}
        isModalOpen={openNewContentModelModal}
        styles={{
          '& .MuiDialogContent-root': {
            height: '360px'
          },
          fontSize: '12px',
          width: '410px'
        }}
        titleContent={<h4 className="modal-title">Create Content Model</h4>}
      >
        <form
          id="new-content-model-form"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between'
          }}
        >
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-content-model-name"
            id="standard-input-content-model-name"
            label="Name"
            onChange={(e) => handleNameChange(e.target.value)}
            size="large"
            value={customTypeDraft.name}
          />
          <p style={{ ...infoBarStyle, color: 'red' }}>{submit ? formErrors.name : ''}</p>
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-content-model-id"
            id="standard-input-content-model-id"
            label="API ID"
            onChange={(e) => updateApiIdInput(e.target.value)}
            size="large"
            value={customTypeDraft.api_id}
          />
          <p style={infoBarStyle}>API ID will be generated according to content model name</p>
          <p style={{ ...infoBarStyle, color: 'red' }}>{submit ? formErrors.apiId : ''}</p>
          <DefaultSelect
            onSelect={(value) => {
              dispatch(
                setCustomTypeDraft({
                  ...customTypeDraft,
                  visibility: value
                })
              );
            }}
            selectStyles={{
              marginBottom: '40px'
            }}
            value={customTypeDraft.visibility}
          >
            {types.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </DefaultSelect>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              fontFamily: 'Poppins',
              justifyContent: 'space-between',
              marginBottom: '15px',
              width: '100%'
            }}
          >
            <p>Set if contents require review</p>
            <Switch
              defaultChecked={false}
              onChange={(isEnabled) => {
                dispatch(
                  setCustomTypeDraft({
                    ...customTypeDraft,
                    isReviewable: isEnabled
                  })
                );
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              fontFamily: 'Poppins',
              justifyContent: 'space-between',
              marginBottom: '15px',
              width: '100%'
            }}
          >
            <p>Should be highlighted in the Menu</p>
            <Switch
              defaultChecked={false}
              onChange={(isHighlighted) => {
                dispatch(
                  setCustomTypeDraft({
                    ...customTypeDraft,
                    isHighlighted
                  })
                );
              }}
            />
          </div>
        </form>
      </Modal>
      <Modal
        footerActions={
          <CButton
            disabled={!isDuplicateSaveEnabled}
            htmlType="submit"
            onClick={(e) => onSaveDuplicate(e)}
            size="large"
            type="primary"
          >
            Create
          </CButton>
        }
        handleClose={onDuplicateCancel}
        isModalOpen={openDuplicateModal}
        styles={{
          fontSize: '12px'
        }}
        titleContent={<span className="modal-title">Duplication</span>}
      >
        <form
          id="new-content-model-form"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between'
          }}
        >
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-content-model-name"
            id="standard-input-content-model-name"
            label="Name"
            onChange={(e) => handleNameChange(e.target.value)}
            size="large"
            value={customTypeDraft.name}
          />
          <p style={{ ...infoBarStyle, color: 'red' }}>{submit ? formErrors.name : ''}</p>
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-content-model-id"
            id="standard-input-content-model-id"
            label="API ID"
            onChange={(e) => updateApiIdInput(e.target.value)}
            size="large"
            value={customTypeDraft.api_id}
          />
          <p style={infoBarStyle}>API ID will be generated according to content model name</p>
          <p style={{ ...infoBarStyle, color: 'red' }}>{submit ? formErrors.apiId : ''}</p>
        </form>
      </Modal>
    </div>
  );
};

export default ContentModel;
